import { components } from "@/api-schema";
import { RelocationOrderTransportMethod } from "@/api-schema-enums";
import { TooltipVariant } from "@/components/ui/tooltip";
import {
  AlertTriangle,
  Ban,
  CarFront,
  Check,
  CirclePause,
  CircleQuestionMark,
  Clock,
  Key,
  LucideIcon,
  PackageX,
  Truck,
} from "lucide-react";

type RelocationOrderStatus = components["schemas"]["RelocationOrder"]["status"];

export enum FitUserType {
  INTERNAL = "INTERNAL",
  EXTERNAL = "EXTERNAL",
}

export enum StatusColor {
  Primary = "primary",
  Secondary = "secondary",
  Error = "error",
  Muted = "muted",
}

const STATUS_COLOR_ICON_CLASSES: Record<StatusColor, { foreground: string; background: string }> = {
  [StatusColor.Primary]: { foreground: "text-primary", background: "bg-primary/10" },
  [StatusColor.Secondary]: { foreground: "text-foreground", background: "bg-secondary" },
  [StatusColor.Error]: { foreground: "text-error", background: "bg-error/10" },
  [StatusColor.Muted]: { foreground: "text-muted-foreground", background: "bg-muted" },
};

const STATUS_COLOR_TOOLTIP_VARIANT: Record<StatusColor, TooltipVariant> = {
  [StatusColor.Primary]: TooltipVariant.OutlinedPrimary,
  [StatusColor.Secondary]: TooltipVariant.OutlinedSecondary,
  [StatusColor.Error]: TooltipVariant.OutlinedError,
  [StatusColor.Muted]: TooltipVariant.Outlined,
};

interface RelocationOrderStatusConfigBase {
  label: string;
  icon: LucideIcon;
  internal: {
    label?: string;
    tooltip: string;
    color: StatusColor;
  };
  external: {
    label?: string;
    tooltip: string;
    color: StatusColor;
  };
}

export interface RelocationOrderStatusConfig {
  label: string;
  labelParams?: Record<string, unknown>;
  icon: LucideIcon;
  tooltip: string;
  tooltipParams?: Record<string, unknown>;
  iconClasses: {
    foreground: string;
    background: string;
  };
  tooltipVariant: TooltipVariant;
}

const STATUS_CONFIGS: Record<RelocationOrderStatus, RelocationOrderStatusConfigBase> = {
  Transport_Company_Awaiting_Pickup_Date: {
    label: "fit:TransportCompanyAwaitingPickupDate",
    icon: Clock,
    internal: {
      tooltip: "fit:TransportCompanyAwaitingPickupDateInternalTooltip",
      color: StatusColor.Secondary,
    },
    external: {
      tooltip: "fit:TransportCompanyAwaitingPickupDateExternalTooltip",
      color: StatusColor.Primary,
    },
  },
  Transport_Company_Awaiting_Ready_For_Transport: {
    label: "fit:TransportCompanyAwaitingReadyForTransport",
    icon: Clock,
    internal: {
      tooltip: "fit:TransportAwaitingReadyForTransportInternalTooltip",
      color: StatusColor.Primary,
    },
    external: {
      tooltip: "fit:TransportAwaitingReadyForTransportExternalTooltip",
      color: StatusColor.Secondary,
    },
  },
  Branch_Awaiting_Pickup: {
    label: "fit:BranchAwaitingPickup",
    icon: Clock,
    internal: {
      tooltip: "fit:BranchAwaitingPickupInternalTooltip",
      color: StatusColor.Secondary,
    },
    external: {
      tooltip: "fit:BranchAwaitingPickupExternalTooltip",
      color: StatusColor.Primary,
    },
  },
  Transport_Company_Rejected_Damaged_Vehicle: {
    label: "fit:TransportCompanyRejectedDamagedVehicle",
    icon: AlertTriangle,
    internal: {
      tooltip: "fit:TransportRejectedDamagedVehicleInternalTooltip",
      color: StatusColor.Error,
    },
    external: {
      tooltip: "fit:TransportRejectedDamagedVehicleExternalTooltip",
      color: StatusColor.Error,
    },
  },
  Transport_Company_Rejected_Missing_Keys: {
    label: "fit:TransportCompanyRejectedMissingKeys",
    icon: Key,
    internal: {
      tooltip: "fit:TransportRejectedMissingKeysInternalTooltip",
      color: StatusColor.Error,
    },
    external: {
      tooltip: "fit:TransportRejectedMissingKeysExternalTooltip",
      color: StatusColor.Error,
    },
  },
  Transport_Company_Rejected_Missing_Vehicle: {
    label: "fit:TransportCompanyRejectedMissingVehicle",
    icon: PackageX,
    internal: {
      tooltip: "fit:TransportCompanyRejectedMissingVehicleInternalTooltip",
      color: StatusColor.Error,
    },
    external: {
      tooltip: "fit:TransportRejectedMissingVehicleExternalTooltip",
      color: StatusColor.Error,
    },
  },
  Transport_Company_In_Transit: {
    label: "fit:TransportCompanyInTransit",
    icon: Truck,
    internal: {
      tooltip: "fit:TransportCompanyInTransitInternalTooltip",
      color: StatusColor.Secondary,
    },
    external: {
      tooltip: "fit:TransportInTransitExternalTooltip",
      color: StatusColor.Primary,
    },
  },
  Transport_Company_Rest_At_Layover: {
    label: "fit:TransportCompanyRestAtLayover",
    icon: CirclePause,
    internal: {
      tooltip: "fit:TransportCompanyRestAtLayoverInternalTooltip",
      color: StatusColor.Secondary,
    },
    external: {
      tooltip: "fit:TransportRestAtLayoverExternalTooltip",
      color: StatusColor.Primary,
    },
  },
  Transport_Company_Awaiting_Delivery_Acceptance: {
    label: "fit:TransportCompanyAwaitingDeliveryAcceptance",
    icon: Clock,
    internal: {
      tooltip: "fit:TransportAwaitingDeliveryAcceptanceInternalTooltip",
      color: StatusColor.Primary,
    },
    external: {
      tooltip: "fit:TransportAwaitingDeliveryAcceptanceExternalTooltip",
      color: StatusColor.Secondary,
    },
  },
  Branch_Rejected_Damaged_Vehicle: {
    label: "fit:BranchRejectedDamagedVehicle",
    icon: AlertTriangle,
    internal: {
      tooltip: "fit:BranchRejectedDamagedVehicleInternalTooltip",
      color: StatusColor.Error,
    },
    external: {
      tooltip: "fit:BranchRejectedDamagedVehicleExternalTooltip",
      color: StatusColor.Error,
    },
  },
  Branch_Rejected_Missing_Keys: {
    label: "fit:BranchRejectedMissingKeys",
    icon: Key,
    internal: {
      tooltip: "fit:BranchRejectedMissingKeysInternalTooltip",
      color: StatusColor.Error,
    },
    external: {
      tooltip: "fit:BranchRejectedMissingKeysExternalTooltip",
      color: StatusColor.Error,
    },
  },
  Branch_Rejected_Missing_Vehicle: {
    label: "fit:BranchRejectedMissingVehicle",
    icon: PackageX,
    internal: {
      tooltip: "fit:BranchRejectedMissingVehicleInternalTooltip",
      color: StatusColor.Error,
    },
    external: {
      tooltip: "fit:BranchRejectedMissingVehicleExternalTooltip",
      color: StatusColor.Error,
    },
  },
  Branch_Rejected_Wrong_Vehicle: {
    label: "fit:BranchRejectedWrongVehicle",
    icon: CarFront,
    internal: {
      tooltip: "fit:BranchRejectedWrongVehicleInternalTooltip",
      color: StatusColor.Error,
    },
    external: {
      tooltip: "fit:BranchRejectedWrongVehicleExternalTooltip",
      color: StatusColor.Error,
    },
  },
  Completed: {
    label: "common:Completed",
    icon: Check,
    internal: {
      tooltip: "fit:CompletedTooltip",
      color: StatusColor.Muted,
    },
    external: {
      tooltip: "fit:CompletedTooltip",
      color: StatusColor.Muted,
    },
  },
  Cancelled: {
    label: "fit:Cancelled",
    icon: Ban,
    internal: {
      tooltip: "fit:CancelledTooltip",
      color: StatusColor.Muted,
    },
    external: {
      tooltip: "fit:CancelledTooltip",
      color: StatusColor.Muted,
    },
  },
};

/**
 * OwnAxle-specific overrides per status.
 * For each status, you can override the label and/or the internal/external tooltip key.
 * Falls back to the base TransportCompany keys if a field is not listed here.
 */
const OWN_AXLE_OVERRIDES: Partial<
  Record<RelocationOrderStatus, { label?: string; internalTooltip?: string; externalTooltip?: string }>
> = {
  Transport_Company_Awaiting_Delivery_Acceptance: {
    label: "fit:OwnAxleAwaitingDeliveryAcceptance",
  },
  Transport_Company_Awaiting_Ready_For_Transport: {
    label: "fit:OwnAxleAwaitingReadyForTransport",
  },
  Transport_Company_In_Transit: {
    label: "fit:OwnAxleInTransit",
    internalTooltip: "fit:OwnAxleInTransitInternalTooltip",
  },
  Transport_Company_Rejected_Damaged_Vehicle: {
    label: "fit:OwnAxleRejectedDamagedVehicle",
  },
  Transport_Company_Rejected_Missing_Keys: {
    label: "fit:OwnAxleRejectedMissingKeys",
  },
  Transport_Company_Rejected_Missing_Vehicle: {
    label: "fit:OwnAxleRejectedMissingVehicle",
    internalTooltip: "fit:OwnAxleRejectedMissingVehicleInternalTooltip",
  },
  Transport_Company_Rest_At_Layover: {
    label: "fit:OwnAxleRestAtLayover",
    internalTooltip: "fit:OwnAxleRestAtLayoverInternalTooltip",
  },
};

export function getStatusConfig(
  status: RelocationOrderStatus,
  userType: FitUserType,
  transportMethod?: RelocationOrderTransportMethod,
): RelocationOrderStatusConfig {
  const baseConfig = STATUS_CONFIGS[status];

  if (!baseConfig) {
    return {
      label: "fit:UnknownStatus",
      labelParams: { status },
      icon: CircleQuestionMark,
      tooltip: "fit:UnknownStatusTooltip",
      tooltipParams: { status },
      iconClasses: STATUS_COLOR_ICON_CLASSES[StatusColor.Muted],
      tooltipVariant: TooltipVariant.Outlined,
    } satisfies RelocationOrderStatusConfig;
  }

  const userConfig: RelocationOrderStatusConfigBase["internal"] =
    userType === FitUserType.INTERNAL ? baseConfig.internal : baseConfig.external;
  const ownAxle = transportMethod === RelocationOrderTransportMethod.OwnAxle ? OWN_AXLE_OVERRIDES[status] : undefined;

  return {
    label: ownAxle?.label ?? userConfig.label ?? baseConfig.label,
    icon: baseConfig.icon,
    tooltip:
      userType === FitUserType.INTERNAL
        ? (ownAxle?.internalTooltip ?? userConfig.tooltip)
        : (ownAxle?.externalTooltip ?? userConfig.tooltip),
    iconClasses: STATUS_COLOR_ICON_CLASSES[userConfig.color],
    tooltipVariant: STATUS_COLOR_TOOLTIP_VARIANT[userConfig.color],
  };
}
